import _ from 'lodash';

import {TimestampKeyTuple} from './types';

// makes sure that timestamp is within the bounds of the tuples
export function isTimestampInBounds(
  tuples: TimestampKeyTuple[],
  targetTimestamp: number
): boolean {
  if (tuples.length === 0) {
    return false;
  }
  return (
    targetTimestamp >= tuples[0][0] &&
    targetTimestamp <= tuples[tuples.length - 1][0]
  );
}

export function getXValueForTimestamp(
  tuples: TimestampKeyTuple[],
  targetTimestamp: number
):
  | {
      bottomIndex: number;
      value: number;
    }
  | undefined {
  // first check if the timestamp is in bounds
  if (!isTimestampInBounds(tuples, targetTimestamp)) {
    return undefined;
  }

  // then check if the timestamp exactly equals the first or last timestamp
  if (targetTimestamp === tuples[0][0]) {
    return {
      bottomIndex: 0,
      value: tuples[0][1],
    };
  } else if (targetTimestamp === tuples[tuples.length - 1][0]) {
    return {
      bottomIndex: tuples.length - 1,
      value: tuples[tuples.length - 1][1],
    };
  }

  // note: because we did the inbounds check we don't need to handle
  // the function returning an index which is outside the array

  const topIndex = findTopTimestampIndex(tuples, targetTimestamp);

  const bottomIndex = topIndex - 1;
  const value = placeTimestamp(
    tuples[bottomIndex],
    tuples[topIndex],
    targetTimestamp
  );
  return {
    bottomIndex,
    value,
  };
}

export function findTopTimestampIndex(
  tuples: TimestampKeyTuple[],
  targetTimestamp: number
): number {
  // implement a binary search to return the index of the first result in the tuples array where the timestamp is greater than the targetTimestamp
  // use the lodash binary search to find the first value in the array where the timestamp is greater
  const index = _.sortedIndexBy(tuples, [targetTimestamp], tuple => tuple[0]);
  return index;
}

export function placeTimestamp(
  bottomTuple: TimestampKeyTuple,
  topTuple: TimestampKeyTuple,
  targetTimestamp: number
): number {
  // first check if the targetTimestamp is exactly equal to the bottomTuple or topTuple
  if (targetTimestamp === bottomTuple[0]) {
    return bottomTuple[1];
  } else if (targetTimestamp === topTuple[0]) {
    return topTuple[1];
  }

  // now we need to place it proportionally between the bottom and top tuples
  // using the xValue
  const delta = topTuple[0] - bottomTuple[0];
  const distance = targetTimestamp - bottomTuple[0];
  const proportion = distance / delta;
  return bottomTuple[1] + (topTuple[1] - bottomTuple[1]) * proportion;
}
